import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type CheckoutPickDiscountSettings = {
  enabled: boolean;
  minSubtotalUsd: number;
  nearGapUsd: number;
  percentOffOneLine: number;
};

export default function WorkspaceCheckoutMarketingSettingsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const [model, setModel] = React.useState<CheckoutPickDiscountSettings>({
    enabled: false,
    minSubtotalUsd: 100,
    nearGapUsd: 15,
    percentOffOneLine: 10,
  });
  const [busy, setBusy] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/checkout-marketing`);
      if (!res.ok) throw new Error('load');
      const data = (await res.json()) as CheckoutPickDiscountSettings;
      setModel(data);
    } catch {
      setError(t('backoffice.checkout_marketing_load_error', 'Unable to load checkout marketing settings.'));
    } finally {
      setBusy(false);
    }
  }, [globalAuthorizedFetch, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/checkout-marketing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(model),
      });
      if (!res.ok) throw new Error('save');
      const data = (await res.json()) as CheckoutPickDiscountSettings;
      setModel(data);
      setSuccess(t('backoffice.checkout_marketing_saved', 'Checkout marketing settings saved.'));
    } catch {
      setError(t('backoffice.checkout_marketing_save_error', 'Unable to save settings.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.checkout_marketing_title', 'Checkout pick discount')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t(
              'backoffice.checkout_marketing_blurb',
              'When a customer’s cart subtotal reaches your minimum (USD), they can choose one product line to receive a percentage discount on that order. Totals are recalculated at checkout.',
            )}
          </p>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          {busy ? <p className="text-sm text-slate-600">{t('common.loading')}</p> : null}
          {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
          {success ? (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</p>
          ) : null}

          <div className="mt-4 space-y-4">
            <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm">
              <span className="font-semibold text-slate-900">
                {t('backoffice.checkout_marketing_enabled', 'Enable offer')}
              </span>
              <input
                type="checkbox"
                checked={model.enabled}
                onChange={(e) => setModel((prev) => ({ ...prev, enabled: e.target.checked }))}
              />
            </label>

            <div className="grid gap-4 sm:grid-cols-3">
              <label className="rounded-lg border border-slate-200 p-3 text-sm">
                <span className="block font-semibold text-slate-900">
                  {t('backoffice.checkout_marketing_min_subtotal', 'Minimum cart subtotal (USD)')}
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                  value={model.minSubtotalUsd}
                  onChange={(e) =>
                    setModel((prev) => ({ ...prev, minSubtotalUsd: Math.max(0, Number(e.target.value) || 0) }))
                  }
                />
                <p className="mt-1 text-xs text-slate-600">
                  {t(
                    'backoffice.checkout_marketing_min_hint',
                    'Subtotal before discounts and delivery must be at or above this amount.',
                  )}
                </p>
              </label>

              <label className="rounded-lg border border-slate-200 p-3 text-sm">
                <span className="block font-semibold text-slate-900">
                  {t('backoffice.checkout_marketing_near_gap', '“Almost there” band (USD)')}
                </span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                  value={model.nearGapUsd}
                  onChange={(e) =>
                    setModel((prev) => ({ ...prev, nearGapUsd: Math.max(0, Number(e.target.value) || 0) }))
                  }
                />
                <p className="mt-1 text-xs text-slate-600">
                  {t(
                    'backoffice.checkout_marketing_near_hint',
                    'We nudge customers when subtotal is within this amount below the minimum.',
                  )}
                </p>
              </label>

              <label className="rounded-lg border border-slate-200 p-3 text-sm">
                <span className="block font-semibold text-slate-900">
                  {t('backoffice.checkout_marketing_percent', 'Percent off one product line')}
                </span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                  value={model.percentOffOneLine}
                  onChange={(e) =>
                    setModel((prev) => ({
                      ...prev,
                      percentOffOneLine: Math.min(100, Math.max(0, Number(e.target.value) || 0)),
                    }))
                  }
                />
                <p className="mt-1 text-xs text-slate-600">
                  {t(
                    'backoffice.checkout_marketing_percent_hint',
                    'Applied only to the customer’s chosen product (all lines for that SKU count together).',
                  )}
                </p>
              </label>
            </div>

            <button
              type="button"
              disabled={saving || busy}
              onClick={() => void save()}
              className="rounded-full bg-slate-900 px-6 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? t('saving') : t('save')}
            </button>
          </div>
        </section>
      </main>
    </MainLayout>
  );
}
